import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ProductionDeclarationMaterialStatusBadge } from '@/features/production-declaration/components/statement/production-declaration-material-status-badge';
import { useUpdateProductionDeclarationMaterialStatus } from '@/services/apis/mutations/production-declaration/use-update-production-declaration-material-status';
import { ProductionDeclarationMaterialStatusCode } from '@/types/core/purchase-order/production-declaration-material-status-code';

type ProductionDeclarationMaterialStatusBannerProps = {
  canEdit: boolean;
  code: ProductionDeclarationMaterialStatusCode;
  materialId: string;
  productionDeclarationId: string;
  token?: string;
};

export function ProductionDeclarationMaterialStatusBanner({
  canEdit,
  code,
  materialId,
  productionDeclarationId,
  token,
}: ProductionDeclarationMaterialStatusBannerProps) {
  const { t } = useTranslation('productionDeclaration');
  const { t: tCommon } = useTranslation('common');
  const { mutate: updateStatus } =
    useUpdateProductionDeclarationMaterialStatus();

  const handleValueChange = (value: string) => {
    updateStatus(
      {
        materialId,
        productionDeclarationId,
        status: value as ProductionDeclarationMaterialStatusCode,
        token,
      },
      {
        onError: () => {
          toast.error(tCommon('error'), {
            description: t('materialStatus.updateError'),
          });
        },
      },
    );
  };

  return (
    <div className='flex items-center justify-between gap-4 rounded-2xl border border-declaration-border bg-white px-6 py-5'>
      <div className='space-y-1'>
        <p className='font-semibold text-declaration-foreground'>
          {t('materialStatus.banner.title')}
        </p>
        <p className='text-declaration-muted-foreground text-sm'>
          {t('materialStatus.banner.description')}
        </p>
      </div>
      {canEdit ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className='cursor-pointer' type='button'>
              <ProductionDeclarationMaterialStatusBadge
                audience='internal'
                chevron
                code={code}
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuRadioGroup
              onValueChange={handleValueChange}
              value={code}
            >
              {Object.values(ProductionDeclarationMaterialStatusCode).map(
                (statusCode) => (
                  <DropdownMenuRadioItem key={statusCode} value={statusCode}>
                    {t(`materialStatus.internal.${statusCode}`)}
                  </DropdownMenuRadioItem>
                ),
              )}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <ProductionDeclarationMaterialStatusBadge
          audience='internal'
          code={code}
        />
      )}
    </div>
  );
}
